/**
 * LIFECYCLE ACTIONS AND THEIR REFUSALS → HEBREW. 08 §6.0 D11, OA-0001 §3.11, DEV-0011.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * "REFUSED" IS NOT AN ANSWER. "IT IS ALREADY SUSPENDED" IS.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `refuse()` in `routes/commercial.ts` puts `currentStatus` and `permittedFrom` on the body, and the
 * company lifecycle route puts `from` and `allowedTransitions` on its own — described there as *"the
 * actionable half: a screen can offer the three buttons that work instead of printing refused"*.
 *
 * This module turns those into sentences. It is the difference between an operator who now knows the
 * job is already done and an operator who opens a support ticket about a working product.
 *
 * ── THE TWO REFUSALS THAT MUST NOT COLLAPSE ─────────────────────────────────────────────────────
 *
 * A SUSPENDED customer and a CLOSED one are not the same state and not the same conversation.
 *
 *   suspended — commercial enforcement. 08 §6.0 D11 makes it deny every action class, AND
 *               OA-0001 §3.11 makes it destroy nothing: every company, every membership and every
 *               grant is exactly where it was, and reinstatement is one action. The customer is
 *               still listed in the console precisely because they have to be findable to reinstate.
 *   closed    — the commercial relationship is over. It is not a deletion either (02 §8: no physical
 *               delete), but it is not a state a screen should offer to undo in one click, and the
 *               sentence says which of the two the operator is looking at.
 *
 * A product that told an operator "מושהה/סגור — הפעולה נדחתה" for both would be answering a question
 * nobody asked.
 *
 * ── AND WHY AN UNKNOWN STATUS IS A MALFORMED REQUEST, NOT A REFUSED TRANSITION ──────────────────
 *
 * The company lifecycle route parses the target status at the boundary and answers 400, not 409, for
 * one the product does not have. DEV-0011 is the reason: a 409 would tell a client that `archived` is
 * a state they LACK PERMISSION to reach, when the product has no such state at all. There is
 * therefore no Hebrew here for a status outside the enum, and the screen never offers one — it builds
 * its buttons from `allowedTransitions`, which the server produced.
 *
 * Pure. Every verdict arrived already made. Nothing here evaluates whether a move is legal.
 */

import type {
  CompanyLifecycleStatus,
  CompanyTransitionRefusal,
  SubscriptionState,
  TenantLifecycleRefusal,
  TenantStatus,
} from './contract';
import { subscriptionStateHe, tenantStatusHe } from './adminVocabulary';

// ── The actions themselves ────────────────────────────────────────────────────────────────────────

/**
 * The three tenant lifecycle verbs the Control Plane can perform, plus what each one does NOT do.
 *
 * The second half of every blast radius is the half an operator is afraid of, and it is stated: none
 * of these touches a company, a membership or a company grant. OA-0001 §3.11 — *"commercial
 * enforcement must not corrupt or delete existing Company data"* — is a promise the screen has to
 * make at the moment the button is pressed, not a paragraph in a contract.
 */
export const TENANT_LIFECYCLE_ACTIONS = ['suspend', 'reactivate', 'close'] as const;
export type TenantLifecycleAction = (typeof TENANT_LIFECYCLE_ACTIONS)[number];

export interface LifecycleActionText {
  /** A VERB, in office Hebrew — 09 §1.3, and `<Modal>`'s `intentHe` is required to be one. */
  readonly intentHe: string;
  /** 09 §2.2: what will happen, including what will NOT. Never left for the operator to infer. */
  readonly blastRadiusHe: string;
  readonly confirmLabelHe: string;
  /** 09 §2.2: an irreversible action is re-selected explicitly and is never the default focus. */
  readonly irreversible: boolean;
  /** Whether the server requires a written reason. It is recorded in the audit row beside the actor. */
  readonly requiresReason: boolean;
}

const TENANT_ACTION_HE: Readonly<Record<TenantLifecycleAction, LifecycleActionText>> = {
  suspend: {
    intentHe: 'השעיית לקוח',
    blastRadiusHe:
      'ההשעיה חוסמת את כל הפעולות במשרד הזה עד לביטולה. אף חברה, אף משתמש ואף הרשאה אינם נמחקים ' +
      'ואינם משתנים, והמשרד ממשיך להופיע ברשימת הלקוחות כדי שאפשר יהיה להחזירו.',
    confirmLabelHe: 'השעיה',
    irreversible: false,
    requiresReason: true,
  },
  reactivate: {
    intentHe: 'ביטול השעיה',
    blastRadiusHe:
      'ביטול ההשעיה מחזיר את המשרד למצב פעיל. הנתונים לא השתנו בזמן ההשעיה, ולכן אין מה לשחזר — ' +
      'הפעולות פשוט נפתחות שוב.',
    confirmLabelHe: 'ביטול השעיה',
    irreversible: false,
    requiresReason: true,
  },
  close: {
    intentHe: 'סגירת לקוח',
    blastRadiusHe:
      'סגירה מסיימת את ההתקשרות המסחרית וחוסמת את כל הפעולות במשרד. הנתונים אינם נמחקים — אין ' +
      'מחיקה פיזית במערכת — אך הפתיחה מחדש אינה פעולה של לחיצה אחת, והיא נעשית מול הבעלים של ' +
      'הפלטפורמה.',
    confirmLabelHe: 'סגירת הלקוח',
    // Not "destructive" in the data sense and still not a click to be taken back: the relationship is
    // what ends, and re-opening it is a commercial decision rather than an undo.
    irreversible: true,
    requiresReason: true,
  },
};

export function tenantLifecycleActionText(action: TenantLifecycleAction): LifecycleActionText {
  return TENANT_ACTION_HE[action];
}

// ── Tenant lifecycle refusals ─────────────────────────────────────────────────────────────────────

export interface LifecycleRefusalText {
  readonly titleHe: string;
  readonly explanationHe: string;
  readonly nextStepHe: string;
  readonly tone: 'warn' | 'error';
  /** Nothing was written. Stated every time, because a half-applied lifecycle move is what is feared. */
  readonly unchangedHe: string;
}

const UNCHANGED_HE = 'לא נשמר שינוי. מצב הלקוח נשאר כפי שהיה לפני הבקשה.';

/**
 * The reason codes `refuse()` can carry that this build has words for.
 *
 * A code with no entry falls through to `GENERIC`, which says the two things that are always true —
 * the server refused and nothing changed — and names the current status if one arrived. It never
 * prints the code (09 §9.1 V3).
 */
const TENANT_REFUSAL_HE: Readonly<Record<string, { readonly titleHe: string; readonly explanationHe: string }>> = {
  tenant_not_found: {
    titleHe: 'הלקוח אינו זמין',
    explanationHe: 'לא הצלחנו להציג את הלקוח שאליו מפנה הפעולה.',
  },
  already_in_state: {
    titleHe: 'הלקוח כבר במצב הזה',
    explanationHe: 'הפעולה שביקשת כבר בוצעה קודם לכן, ולכן אין מה לשנות.',
  },
  invalid_transition: {
    titleHe: 'המעבר אינו אפשרי מהמצב הנוכחי',
    explanationHe: 'המעבר שביקשת מוגדר רק ממצבים מסוימים, והלקוח אינו נמצא באחד מהם.',
  },
  reason_required: {
    titleHe: 'חסרה סיבה לפעולה',
    explanationHe: 'פעולת מחזור חיים נרשמת ביומן יחד עם הסיבה שלה, ולכן היא אינה מתבצעת בלעדיה.',
  },
  subscription_not_found: {
    titleHe: 'ללקוח אין מנוי',
    explanationHe: 'הפעולה חלה על מנוי, ולא נמצא מנוי ללקוח הזה.',
  },
  self_approval_not_permitted: {
    titleHe: 'לא ניתן לאשר בקשה שהוגשה בידיך',
    explanationHe:
      'הפעולה הזו דורשת שני אנשים: מי שמבקש ומי שמאשר. אישור עצמי היה מבטל את הבקרה הכפולה.',
  },
};

const GENERIC_TENANT_REFUSAL = {
  titleHe: 'הפעולה לא בוצעה',
  explanationHe: 'השרת סירב לבקשה ולא נשמר שינוי.',
};

/** Exposed for the mirror test only. */
export function mappedTenantRefusalReasons(): readonly string[] {
  return Object.keys(TENANT_REFUSAL_HE);
}

/**
 * A refusal, with the CURRENT state and the permitted origins spelled out where the server sent them.
 *
 * The next step is built from `permittedFrom` rather than being a fixed sentence, because "suspension
 * is permitted from פעיל or תקופת ניסיון" is an instruction and "the transition is invalid" is not.
 */
export function tenantLifecycleRefusalText(refusal: TenantLifecycleRefusal): LifecycleRefusalText {
  const copy = TENANT_REFUSAL_HE[refusal.reason] ?? GENERIC_TENANT_REFUSAL;
  const current =
    refusal.currentStatus === null
      ? null
      : `מצב הלקוח כעת: ${tenantStatusHe(refusal.currentStatus).labelHe}.`;
  const permitted =
    refusal.permittedFrom.length === 0
      ? null
      : `הפעולה אפשרית מהמצבים: ${refusal.permittedFrom.map((s) => tenantStatusHe(s).labelHe).join(' · ')}.`;

  return {
    titleHe: copy.titleHe,
    explanationHe: [copy.explanationHe, current].filter((s): s is string => s !== null).join(' '),
    nextStepHe:
      permitted ??
      'אפשר לרענן את כרטיס הלקוח ולראות את מצבו העדכני לפני ניסיון נוסף.',
    tone: refusal.reason === 'already_in_state' ? 'warn' : 'error',
    unchangedHe: UNCHANGED_HE,
  };
}

/** The same shape for a forced subscription state change, whose permitted set is a state list. */
export function subscriptionStatesHe(states: readonly SubscriptionState[]): string {
  return states.map((s) => subscriptionStateHe(s).labelHe).join(' · ');
}

// ── Company lifecycle ─────────────────────────────────────────────────────────────────────────────

/**
 * The eight lifecycle states a company can be in, as office Hebrew — and `<LifecycleChip>` already
 * renders them. What this adds is the VERB for moving INTO each one, which is a different word:
 * a company is not "made dormant", the office "מעביר לתרדמת". 09 §1.3 again — a verb is an action.
 */
const TRANSITION_VERB_HE: Readonly<Record<CompanyLifecycleStatus, string>> = {
  prospect: 'החזרה למצב לקוח פוטנציאלי',
  active: 'החזרה לפעילות',
  dormant: 'העברה לתרדמת',
  former_client: 'סימון כלקוח לשעבר',
  merged_into: 'סימון כמוזגה לחברה אחרת',
  quarantined: 'העברה להסגר',
  source_unavailable: 'סימון שמקור הנתונים אינו זמין',
  review_required: 'סימון שנדרשת בדיקה',
};

export function companyTransitionVerbHe(to: CompanyLifecycleStatus): string {
  return TRANSITION_VERB_HE[to];
}

/** Exposed for the mirror test only — a status with no verb is a button with no label. */
export function mappedTransitionTargets(): readonly string[] {
  return Object.keys(TRANSITION_VERB_HE);
}

/**
 * `merged_into` is the one transition that CANNOT be expressed without a second company, and the
 * server's input union cannot be built without one. The screen therefore has to ask, and this flag is
 * how it knows to — rather than sending a merge with no target and reading the 400 back.
 */
export function transitionNeedsMergeTarget(to: CompanyLifecycleStatus): boolean {
  return to === 'merged_into';
}

const COMPANY_REFUSAL_HE: Readonly<Record<string, { readonly titleHe: string; readonly explanationHe: string }>> = {
  company_not_found: {
    titleHe: 'החברה אינה זמינה',
    explanationHe: 'לא הצלחנו להציג את החברה שאליה מפנה הפעולה.',
  },
  invalid_transition: {
    titleHe: 'המעבר אינו אפשרי מהמצב הנוכחי',
    explanationHe: 'מצבי החברה עוברים במסלול קבוע, והמעבר שביקשת אינו יוצא מהמצב שבו החברה נמצאת.',
  },
  already_in_state: {
    titleHe: 'החברה כבר במצב הזה',
    explanationHe: 'המצב שביקשת הוא המצב הנוכחי של החברה, ולכן אין מה לשנות.',
  },
  merge_target_required: {
    titleHe: 'חסרה החברה הקולטת',
    explanationHe: 'סימון מיזוג מחייב לציין לאיזו חברה מוזגה החברה הזו.',
  },
  merge_target_not_found: {
    titleHe: 'החברה הקולטת אינה נמצאת במשרד',
    explanationHe: 'החברה שאליה מוזגה החברה הזו חייבת להיות חברה קיימת באותו משרד.',
  },
};

const GENERIC_COMPANY_REFUSAL = {
  titleHe: 'מצב החברה לא שונה',
  explanationHe: 'השרת סירב לבקשה ולא נשמר שינוי.',
};

/** Exposed for the mirror test only. */
export function mappedCompanyRefusalReasons(): readonly string[] {
  return Object.keys(COMPANY_REFUSAL_HE);
}

export interface CompanyTransitionRefusalText extends LifecycleRefusalText {
  /**
   * The moves that WOULD work, from where the company actually is. Empty means the server sent none,
   * and the screen then offers nothing rather than the list it was already showing — that list was
   * computed before the refusal and is exactly what has just been proven wrong.
   */
  readonly allowedTransitions: readonly CompanyLifecycleStatus[];
}

export function companyTransitionRefusalText(
  refusal: CompanyTransitionRefusal,
  lifecycleLabelHe: (status: CompanyLifecycleStatus) => string,
): CompanyTransitionRefusalText {
  const copy = COMPANY_REFUSAL_HE[refusal.reason] ?? GENERIC_COMPANY_REFUSAL;
  const from =
    refusal.from === null ? null : `מצב החברה כעת: ${lifecycleLabelHe(refusal.from)}.`;

  return {
    titleHe: copy.titleHe,
    explanationHe: [copy.explanationHe, from].filter((s): s is string => s !== null).join(' '),
    nextStepHe:
      refusal.allowedTransitions.length === 0
        ? 'אפשר לרענן את כרטיס החברה ולראות את מצבה העדכני לפני ניסיון נוסף.'
        : `מהמצב הנוכחי אפשר לעבור אל: ${refusal.allowedTransitions.map(lifecycleLabelHe).join(' · ')}.`,
    tone: refusal.reason === 'already_in_state' ? 'warn' : 'error',
    unchangedHe: 'לא נשמר שינוי. מצב החברה נשאר כפי שהיה לפני הבקשה.',
    allowedTransitions: refusal.allowedTransitions,
  };
}
